import {
  CustomMappingOption,
  CustomMappingType,
  IFailedCustomMappingRecord,
  ITranslationMap,
  type ICustomMapping,
  type ICustomMappingSourceOption,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { PatientStatus } from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  WithPrimaryKey,
  XSLXImporterExporter,
  asyncForEach,
  getEnumValues,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { of } from 'rxjs';
import { BaseCustomMappingHandler } from '../../../base-custom-mapping-handler';
import { CustomMapping } from '../../../custom-mapping';
import { TranslationMapHandler } from '../../../translation-map';
import {
  PatientStatusSourceEntity,
  type ID4WPatientStatus,
} from '../../source/entities/patient-status';
import { PatientStatusesToXSLX } from './patient-statuses-to-xlsx';
import { XSLXToPatientStatuses } from './xlsx-to-patient-statuses';
import { ID4WPatient } from '../../source/entities/patient';
import { PracticeMigration } from '../../../practice-migrations';

export const PATIENT_STATUS_CUSTOM_MAPPING_TYPE = 'patientStatusMapping';

export const PATIENT_STATUS_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Patient Statuses',
    description: `Used for mapping patient statuses to Principle.`,
    type: PATIENT_STATUS_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.SelectionList,
});

export class D4WPatientStatusMappingHandler extends BaseCustomMappingHandler<
  object,
  PatientStatus
> {
  customMapping = PATIENT_STATUS_MAPPING;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const patientStatusOptions = new PatientStatusSourceEntity();
    const records = await patientStatusOptions
      .getAllRecords$(migration)
      .toPromise();
    return sortBy(
      records
        .map((record) => record.data)
        .map((record) => ({
          label: record.name,
          value: patientStatusOptions.getSourceRecordId(record).toString(),
        })),
      'label'
    );
  }

  async getSelectionListOptions(
    _migration: WithRef<IPracticeMigration>
  ): Promise<CustomMappingOption[]> {
    return snapshot(
      of(
        getEnumValues(PatientStatus)
          .sort()
          .map((status) => ({
            value: status,
            description: status,
            hasAssociatedValue: false,
          }))
      )
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, records, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(fileName, records, translator);
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, records, translator } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(fileName, records, translator);
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToPatientStatuses()
    );

    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    await asyncForEach(items, async (item) => {
      const matchingOption = sourceOptions.find(
        (sourceOption) => sourceOption.value === item.id
      );

      if (!matchingOption) {
        return;
      }

      const label = matchingOption.label;
      const value = matchingOption.value;
      const mapTo = item.mapTo;

      if (!mapTo) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find status for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: value,
          sourceLabel: label,
          reason: `Mapped status not found`,
        });
        return;
      }

      await this.upsertRecord(
        {
          destinationValue: { value: mapTo },
          sourceIdentifier: value,
          sourceLabel: label,
        },
        translationMap
      );
    });

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    records: ID4WPatientStatus[];
    translator: PatientStatusesToXSLX;
  }> {
    const fileName = this.getFileName();
    const translationMap = new TranslationMapHandler(migration);
    const records = await this._getPatientStatusOptions(migration);
    const translator = new PatientStatusesToXSLX(
      await this.getRecords(translationMap),
      defaultMappings
    );
    return { fileName, records, translator };
  }

  private async _getPatientStatusOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ID4WPatientStatus[]> {
    const patientStatusOptions = new PatientStatusSourceEntity();
    const records = await patientStatusOptions
      .getAllRecords$(migration)
      .toPromise();
    return sortBy(
      records.map((record) => record.data),
      'name'
    );
  }
}

const defaultMappings: Record<string, PatientStatus> = {
  None: PatientStatus.Active,
  Casual: PatientStatus.Active,
  ['Casual Visitor']: PatientStatus.Active,
  Cosmetic: PatientStatus.Active,
  ['Event Driven']: PatientStatus.Active,
  Family: PatientStatus.Active,
  Inactive: PatientStatus.Inactive,
  ['Inactive Deceased']: PatientStatus.Deceased,
  ['Inactive Transferred']: PatientStatus.Inactive,
  Proactive: PatientStatus.Active,
  Reactive: PatientStatus.Active,
  Regenerative: PatientStatus.Active,
  Regular: PatientStatus.Active,
};

export function resolveD4WPatientStatus(
  mappings: WithPrimaryKey<ITranslationMap<object, PatientStatus>>[],
  patient: ID4WPatient
): PatientStatus {
  // eslint-disable-next-line no-null/no-null
  if (patient.category_id === null && !patient.category_name) {
    return PatientStatus.Active;
  }
  const statusMapping = mappings.find(
    (mapping) => mapping.sourceIdentifier === patient.category_id?.toString()
  );
  if (!statusMapping || !statusMapping.destinationValue) {
    return patient.category_name
      ? defaultMappings[patient.category_name] ?? PatientStatus.Active
      : PatientStatus.Active;
  }

  return statusMapping.destinationValue.value;
}
